import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { pageHorizontalPadding } from '../../components/Layout/Layout';
import { colors } from '../../themes/typography';
import { responsiveTypography, responsiveSpacing } from '../../themes/responsive';
import ExperimentBoard from './components/ExperimentBoard';
import ExperimentFilters from './components/ExperimentFilters';
import ExperimentBoardSkeleton from './components/ExperimentSkeleton';
import RejectReasonDialog from '../../components/RejectReasonDialog/RejectReasonDialog';
import { 
  fetchFilterOptions,
  fetchExperiments,
  acceptExperiment,
  rejectExperiment,
  beginExperiment,
  updateExperimentStatus,
  fetchRouteOptions
} from './services/experimentManagementApi';
import { 
  fetchAlertFilterOptions,
  fetchAlerts,
  markAlertAsBroken,
  markAlertAsResolved,
  markAlertAsNotBroken
} from './services/alertManagementApi';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { formatLocalDate } from '../../utils/dateUtils';
import AppliedPricingDialog from '../../components/shared/AppliedPricingDialog';
import { getApiErrorMessage } from '../../utils/apiError';

// Overview Tab Component (main content) - Now handles both experiments and alerts
export const OverviewTab = ({ isAlertsMode = false }) => {
  
  const { t } = useTranslation();
  const location = useLocation();
  const { currentUser } = useAuth();
  const filtersRef = React.useRef(null);

  // Initialize state without sessionStorage cache - always start fresh
  const [state, setState] = React.useState(() => {
    return {
      filterMode: 'machine',
      filters: {
        group: '', location: '', machine: '', route: '', product: '', alertType: '',
        dateRange: { start: null, end: null }
      },
      filterOptions: { groups: [], alertTypes: [] },
      visibleGroups: [],
      availableLocations: [],
      availableMachines: [],
      availableRoutes: [],
      availableAlertTypes: [],
      productOptions: [],
      experiments: isAlertsMode ? {
        pending: [], under_review: [], resolved: []
      } : {
        pending: [], accepted: [], running: [], finished: [], rejected: []
      },
      // Add alerts-specific state when in alerts mode
      alerts: isAlertsMode ? {
        pending: [], under_review: [], resolved: []
      } : null
    };
  });

  // Destructure state for easier access
  const { 
    filterMode, filters, filterOptions, visibleGroups,
    availableLocations, availableMachines, availableRoutes,
    availableAlertTypes, productOptions, experiments, alerts 
  } = state;
  
  // Loading states with guards to prevent duplicate calls
  const [loading, setLoading] = React.useState(false);
  const [loadingFilters, setLoadingFilters] = React.useState(true);
  const loadingRef = React.useRef(false); // Prevent concurrent data loads
  const filterLoadingRef = React.useRef(false); // Prevent concurrent filter loads
  const filterOptionsCache = React.useRef({}); // Cache filter options by mode
  const [routesLoading, setRoutesLoading] = React.useState(false);
  const routesCache = React.useRef({});

  const formatDateForKey = React.useCallback((date) => {
    if (!date) return '';
    return formatLocalDate(date);
  }, []);

  const haveSameIds = React.useCallback((current = [], next = []) => {
    if (current.length !== next.length) {
      return false;
    }

    for (let index = 0; index < current.length; index += 1) {
      const currentId = current[index]?.id;
      const nextId = next[index]?.id;
      if (String(currentId) !== String(nextId)) {
        return false;
      }
    }

    return true;
  }, []);
  
  // Add mount/unmount tracking
  React.useEffect(() => {
    return () => {};
  }, []);
  
  // Track mode changes
  React.useEffect(() => {
  }, [isAlertsMode]);
  
  // Toast notification state
  const [toast, setToast] = React.useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'warning', 'info'
  });

  // Reject dialog state
  const [showRejectDialog, setShowRejectDialog] = React.useState(false);
  const [currentExperimentId, setCurrentExperimentId] = React.useState(null);
  const [priceReview, setPriceReview] = React.useState(null);


  // Initialize global scroll state
  React.useEffect(() => {
    if (!window.savedColumnScroll) {
      window.savedColumnScroll = null;
    }
  }, []);

  // Function to refresh data after any mutation - unified for experiments and alerts
  const refreshDataAfterMutation = React.useCallback(async () => {
    if (filters.dateRange.start && filters.dateRange.end) {
      // Inline loadData to avoid circular dependency
      setLoading(true);
      try {
        if (isAlertsMode) {
          // Load alerts data
          const alertsData = await fetchAlerts(filters, currentUser.customer_id);
          setState(prev => ({
            ...prev,
            alerts: alertsData,
            experiments: alertsData // Also update experiments for board compatibility
          }));
        } else {
          // Load experiments data (existing logic)
          let experimentsData;
          
          if (filterMode === 'product' && filters.product) {
            // Use product-specific endpoint
            const token = localStorage.getItem('access_token');
            const response = await axios.get(
              `${API_BASE_URL}/products/${filters.product}/experiments-management?clientId=${currentUser.customer_id}&startDate=${formatLocalDate(filters.dateRange.start)}&endDate=${formatLocalDate(filters.dateRange.end)}`,
              { headers: createAuthHeader(token) }
            );
            experimentsData = response.data;
          } else if (filterMode === 'machine') {
            // Use existing machine-based endpoint
            experimentsData = await fetchExperiments(filters, currentUser.customer_id);
          } else {
            // No valid filters for current mode
            experimentsData = {
              pending: [],
              accepted: [],
              running: [],
              finished: [],
              rejected: []
            };
          }
          
          setState(prev => ({
            ...prev,
            experiments: experimentsData
          }));
        }
      } catch (error) {
        // Silent error handling in refresh
      } finally {
        setLoading(false);
      }
      
      // Restore main scroll container position after data loads
      if (window.savedColumnScroll) {
        setTimeout(() => {
          const mainScrollContainer = document.querySelector('.css-4z6nar');
          if (mainScrollContainer && window.savedColumnScroll.scrollTop !== undefined) {
            mainScrollContainer.scrollTop = window.savedColumnScroll.scrollTop;
          }
          window.savedColumnScroll = null; // Clear after use
        }, 100);
      }
    }
  }, [filterMode, filters, isAlertsMode, currentUser.customer_id]);

  // Listen for experiment state changes that require data refresh
  React.useEffect(() => {
    const handleExperimentMutation = (event) => {
      refreshDataAfterMutation();
    };

    // Listen for custom events from experiment cards/components
    window.addEventListener('experimentMutation', handleExperimentMutation);
    
    return () => {
      window.removeEventListener('experimentMutation', handleExperimentMutation);
    };
  }, [refreshDataAfterMutation]);



  // Toast helper functions
  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  // Load filter options and set defaults on mount - unified for both modes with caching
  React.useEffect(() => {
    
    const loadFilterOptions = async () => {
      // Prevent concurrent filter loading
      if (filterLoadingRef.current) {
        return;
      }
      
      // Check cache first
      const cacheKey = isAlertsMode ? 'alerts' : 'experiments';
      if (filterOptionsCache.current[cacheKey]) {
        setState(prev => ({
          ...prev,
          filterOptions: filterOptionsCache.current[cacheKey],
          visibleGroups: filterOptionsCache.current[cacheKey].groups || []
        }));
        setLoadingFilters(false);
        return;
      }
      
      filterLoadingRef.current = true;
      
      try {
        // Use appropriate API based on mode
        const options = isAlertsMode 
          ? await fetchAlertFilterOptions(currentUser.customer_id)
          : await fetchFilterOptions(currentUser.customer_id);
        
        // Cache the options
        filterOptionsCache.current[cacheKey] = options;
        
        // Check for filters passed from navigation state (from machines or products page)
        const passedFilters = location.state?.filters;
        const passedFilterMode = location.state?.filterMode;
        
        // Set defaults: current month start to next month end for date range
        const currentDate = new Date();
        const startOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfNextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);
        
        setState(prev => ({
          ...prev,
          filterOptions: options,
          visibleGroups: options.groups || [],
          availableAlertTypes: (options.alertTypes || []).filter(type => type !== 'my_test'),
          filterMode: 'machine',
          filters: {
            ...prev.filters,
            // Apply passed filters if available, otherwise use defaults
            group: passedFilters?.group || '',
            location: passedFilters?.location || '',
            machine: passedFilters?.machine || '',
            route: passedFilters?.route || '',
            product: '',
            alertType: passedFilters?.alertType || '',
            dateRange: {
              start: startOfCurrentMonth,
              end: endOfNextMonth
            }
          }
        }));

        // Load product options for product filter mode
        // Product mode disabled for experiment management
      } catch (error) {
        console.error('Error loading filter options:', error);
      } finally {
        setLoadingFilters(false);
        filterLoadingRef.current = false; // Reset loading guard
      }
    };
    
    if (currentUser?.customer_id) {
      loadFilterOptions();
    }
  }, [currentUser?.customer_id, isAlertsMode]); // Fixed dependencies


  // When group changes, update available locations
  React.useEffect(() => {
    if (!visibleGroups.length) {
      setState(prev => ({
        ...prev,
        availableLocations: [],
        availableMachines: [],
        filters: {
          ...prev.filters,
          location: '',
          machine: ''
        }
      }));
      return;
    }

    if (!filters.group) {
      setState(prev => ({
        ...prev,
        availableLocations: [],
        availableMachines: [],
        filters: {
          ...prev.filters,
          location: '',
          machine: ''
        }
      }));
      return;
    }

    const selectedGroup = visibleGroups.find(g => g.id === filters.group);
    if (!selectedGroup) {
      setState(prev => ({
        ...prev,
        availableLocations: [],
        availableMachines: [],
        filters: {
          ...prev.filters,
          group: '',
          location: '',
          machine: ''
        }
      }));
      return;
    }

    const groupLocations = [];
    selectedGroup.clusters?.forEach(cluster => {
      (cluster.locations || []).forEach(location => {
        groupLocations.push(location);
      });
    });

    const existingLocation = groupLocations.find(location => location.id === filters.location);
    const nextLocation = existingLocation ? filters.location : '';

    const machinesForLocation = existingLocation ? (existingLocation.machines || []) : [];
    const machineExists = machinesForLocation.some(machine => String(machine.id) === String(filters.machine));
    const nextMachine = machineExists ? filters.machine : '';

    setState(prev => {
      const shouldUpdateLocations = !haveSameIds(prev.availableLocations, groupLocations);
      const shouldUpdateMachines = !haveSameIds(prev.availableMachines, machinesForLocation);
      const shouldUpdateLocationValue = prev.filters.location !== nextLocation;
      const shouldUpdateMachineValue = prev.filters.machine !== nextMachine;

      if (!shouldUpdateLocations && !shouldUpdateMachines && !shouldUpdateLocationValue && !shouldUpdateMachineValue) {
        return prev;
      }

      return {
        ...prev,
        availableLocations: groupLocations,
        availableMachines: machinesForLocation,
        filters: {
          ...prev.filters,
          group: prev.filters.group,
          location: nextLocation,
          machine: nextMachine
        }
      };
    });
  }, [filters.group, filters.location, filters.machine, visibleGroups, haveSameIds]);

  // When location changes, update available machines
  React.useEffect(() => {
    if (!visibleGroups.length || !filters.group || !filters.location) {
      setState(prev => ({
        ...prev,
        availableMachines: [],
        filters: {
          ...prev.filters,
          machine: ''
        }
      }));
      return;
    }

    const selectedGroup = visibleGroups.find(g => g.id === filters.group);
    if (!selectedGroup) {
      setState(prev => ({
        ...prev,
        availableMachines: [],
        filters: {
          ...prev.filters,
          machine: ''
        }
      }));
      return;
    }

    let selectedLocation = null;
    selectedGroup.clusters?.forEach(cluster => {
      const location = cluster.locations?.find(l => l.id === filters.location);
      if (location) {
        selectedLocation = location;
      }
    });

    if (!selectedLocation) {
      setState(prev => ({
        ...prev,
        availableMachines: [],
        filters: {
          ...prev.filters,
          machine: ''
        }
      }));
      return;
    }

    const machines = selectedLocation.machines || [];
    const machineExists = machines.some(machine => String(machine.id) === String(filters.machine));
    const nextMachine = machineExists ? filters.machine : '';

    setState(prev => {
      const shouldUpdateMachines = !haveSameIds(prev.availableMachines, machines);
      const shouldUpdateMachineValue = prev.filters.machine !== nextMachine;

      if (!shouldUpdateMachines && !shouldUpdateMachineValue) {
        return prev;
      }

      return {
        ...prev,
        availableMachines: machines,
        filters: {
          ...prev.filters,
          machine: nextMachine
        }
      };
    });
  }, [filters.location, filters.machine, visibleGroups, filters.group, haveSameIds]);

  React.useEffect(() => {
    const baseGroups = filterOptions.groups || [];
    if (!filters.route) {
      setState(prev => {
        if (prev.visibleGroups === baseGroups) {
          return prev;
        }
        return {
          ...prev,
          visibleGroups: baseGroups
        };
      });
      return;
    }

    const selectedRoute = availableRoutes.find(route => String(route.id) === String(filters.route));
    if (!selectedRoute || !Array.isArray(selectedRoute.stores) || selectedRoute.stores.length === 0) {
      setState(prev => ({
        ...prev,
        visibleGroups: [],
        filters: {
          ...prev.filters,
          group: '',
          location: '',
          machine: ''
        },
        availableLocations: [],
        availableMachines: []
      }));
      return;
    }

    const routeMachineIds = new Set(
      selectedRoute.stores
        .map(store => store?.id)
        .filter(id => id !== undefined && id !== null)
        .map(id => String(id))
    );

    const filteredGroups = baseGroups
      .map(group => {
        const filteredClusters = (group?.clusters || [])
          .map(cluster => {
            const filteredLocations = (cluster?.locations || [])
              .map(location => {
                const filteredMachines = (location?.machines || [])
                  .filter(machine => routeMachineIds.has(String(machine.id)));
                
                if (!filteredMachines.length) {
                  return null;
                }

                return {
                  ...location,
                  machines: filteredMachines
                };
              })
              .filter(Boolean);

            if (!filteredLocations.length) {
              return null;
            }

            return {
              ...cluster,
              locations: filteredLocations
            };
          })
          .filter(Boolean);

        if (!filteredClusters.length) {
          return null;
        }

        return {
          ...group,
          clusters: filteredClusters
        };
      })
      .filter(Boolean);

    setState(prev => {
      const existingGroup = filteredGroups.find(group => group.id === prev.filters.group);
      const nextGroup = existingGroup ? prev.filters.group : '';

      let nextLocation = '';
      let nextMachine = '';

      if (existingGroup) {
        const groupLocations = [];
        existingGroup.clusters?.forEach(cluster => {
          (cluster.locations || []).forEach(location => {
            groupLocations.push(location);
          });
        });

        const existingLocation = groupLocations.find(location => location.id === prev.filters.location);
        if (existingLocation) {
          nextLocation = prev.filters.location;

          const machineExists = (existingLocation.machines || []).some(
            machine => String(machine.id) === String(prev.filters.machine)
          );

          if (machineExists) {
            nextMachine = prev.filters.machine;
          }
        }
      }

      return {
        ...prev,
        visibleGroups: filteredGroups,
        filters: {
          ...prev.filters,
          group: nextGroup,
          location: nextLocation,
          machine: nextMachine
        }
      };
    });
  }, [filters.route, availableRoutes, filterOptions.groups]);

  React.useEffect(() => {
    if (filterMode !== 'machine') {
      setRoutesLoading(false);
      setState(prev => ({
        ...prev,
        availableRoutes: [],
        visibleGroups: filterOptions.groups || [],
        filters: {
          ...prev.filters,
          route: ''
        }
      }));
      return;
    }

    if (!filters.dateRange.start || !filters.dateRange.end || !currentUser?.customer_id) {
      setRoutesLoading(false);
      setState(prev => ({
        ...prev,
        availableRoutes: [],
        visibleGroups: filterOptions.groups || [],
        filters: {
          ...prev.filters,
          route: ''
        }
      }));
      return;
    }

    const cacheKey = [
      formatDateForKey(filters.dateRange.start),
      formatDateForKey(filters.dateRange.end),
      filters.group || '',
      filters.location || '',
      filters.machine || ''
    ].join('|');

    const cachedRoutes = routesCache.current[cacheKey];
    if (cachedRoutes) {
      setState(prev => ({
        ...prev,
        availableRoutes: cachedRoutes,
        filters: {
          ...prev.filters,
          route: cachedRoutes.some(route => String(route.id) === String(prev.filters.route))
            ? prev.filters.route
            : ''
        }
      }));
      return;
    }

    let isActive = true;

    const loadRoutes = async () => {
      setRoutesLoading(true);
      try {
        const routes = await fetchRouteOptions(filters, currentUser.customer_id);
        if (!isActive) return;
        routesCache.current[cacheKey] = routes;
        setState(prev => ({
          ...prev,
          availableRoutes: routes,
          filters: {
            ...prev.filters,
            route: routes.some(route => String(route.id) === String(prev.filters.route))
              ? prev.filters.route
              : ''
          }
        }));
      } catch (error) {
        if (!isActive) return;
        setState(prev => ({
          ...prev,
          availableRoutes: [],
          filters: {
            ...prev.filters,
            route: ''
          }
        }));
      } finally {
        if (isActive) {
          setRoutesLoading(false);
        }
      }
    };

    loadRoutes();

    return () => {
      isActive = false;
    };
  }, [
    filterMode,
    filters.group,
    filters.location,
    filters.machine,
    filters.dateRange.start,
    filters.dateRange.end,
    isAlertsMode,
    currentUser?.customer_id,
    formatDateForKey,
    filterOptions.groups
  ]);

  // Load product options function
  const loadProductOptions = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_BASE_URL}/all-products?clientId=${currentUser.customer_id}`,
        { headers: createAuthHeader(token) }
      );
      setState(prev => ({
        ...prev,
        productOptions: response.data.products
      }));
    } catch (error) {
    }
  };

  // Load data when filters change (auto-search) - unified for experiments and alerts
  React.useEffect(() => {
    
    if (filters.dateRange.start && filters.dateRange.end) {
      const timeoutId = setTimeout(() => {
        loadData();
      }, 300);
      
      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      // Clear data if date range is missing
      const emptyData = isAlertsMode ? {
        pending: [], under_review: [], resolved: []
      } : {
        pending: [], accepted: [], running: [], finished: [], rejected: []
      };
      
      setState(prev => ({
        ...prev,
        experiments: emptyData,
        ...(isAlertsMode && { alerts: emptyData })
      }));
    }
  }, [filterMode, filters.group, filters.location, filters.machine, filters.product, filters.alertType, filters.route, filters.dateRange, isAlertsMode]);

  // Unified data loading function for both experiments and alerts
  const loadData = async () => {
    
    // Prevent concurrent data loading
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    
    setLoading(true);
    try {
      if (isAlertsMode) {
        
        // Load alerts data
        const alertsData = await fetchAlerts(filters, currentUser.customer_id);
        
        
        setState(prev => ({
          ...prev,
          alerts: alertsData,
          experiments: alertsData // Also update experiments for board compatibility
        }));
      } else {
        // Load experiments data (existing logic)
        let experimentsData;
        
        if (filterMode === 'product' && filters.product) {
          // Use product-specific endpoint
          const token = localStorage.getItem('access_token');
          const response = await axios.get(
            `${API_BASE_URL}/products/${filters.product}/experiments-management?clientId=${currentUser.customer_id}&startDate=${formatLocalDate(filters.dateRange.start)}&endDate=${formatLocalDate(filters.dateRange.end)}`,
            { headers: createAuthHeader(token) }
          );
          experimentsData = response.data;
        } else if (filterMode === 'machine') {
          // Use existing machine-based endpoint
          experimentsData = await fetchExperiments(filters, currentUser.customer_id);
        } else {
          // No valid filters for current mode
          experimentsData = {
            pending: [],
            accepted: [],
            running: [],
            finished: [],
            rejected: []
          };
        }
        
        setState(prev => ({
          ...prev,
          experiments: experimentsData
        }));
      }
    } catch (error) {
      const errorMessage = isAlertsMode ? 'Failed to load alerts' : t('failedToLoadExperiments');
      showToast(errorMessage, 'error');
      
      // Set empty data on error
      const emptyData = isAlertsMode ? {
        pending: [], under_review: [], resolved: []
      } : {
        pending: [], accepted: [], running: [], finished: [], rejected: []
      };
      
      setState(prev => ({
        ...prev,
        [isAlertsMode ? 'alerts' : 'experiments']: emptyData,
        experiments: emptyData // Always update experiments for board compatibility
      }));
    } finally {
      setLoading(false);
      loadingRef.current = false; // Reset loading guard
    }
  };

  const handleFilterChange = (filterType, value) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [filterType]: value,
        ...(filterType === 'route'
          ? { group: '', location: '', machine: '' }
          : {})
      },
      ...(filterType === 'route'
        ? { availableLocations: [], availableMachines: [] }
        : {})
    }));
  };

  const handleDateRangeChange = (dateRange) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        dateRange
      }
    }));
  };

  const handleFilterModeChange = () => {
    // Product view disabled; keep machine view active
    setState(prev => ({
      ...prev,
      filterMode: 'machine',
      filters: {
        ...prev.filters,
        product: ''
      }
    }));
  };

  // `followUpIds`: cola de un cambio de estado masivo. Si ESTE experimento abre el diálogo de
  // precios, los que vienen detrás viajan con el diálogo y se retoman al confirmar — sin esto el
  // bucle del bulk se cortaba en el primer diálogo y los demás seleccionados se quedaban sin
  // aplicar en silencio.
  const handleExperimentAction = async (experimentId, action, reason = '', skipRefresh = false, pricingOverride = null, followUpIds = []) => {
    // Handle reject action with dialog if no reason provided
    if (action === 'reject' && !reason) {
      setCurrentExperimentId(experimentId);
      setShowRejectDialog(true);
      return;
    }

    try {
      if (!pricingOverride && ['accept', 'accepted'].includes(action)) {
        const token = localStorage.getItem('access_token');
        const response = await axios.post(`${API_BASE_URL}/experiments/pricing-preview?clientId=${currentUser.customer_id}`, { experimentId }, { headers: createAuthHeader(token) });
        if (response.data?.pricing) {
          setPriceReview({ experimentId, action, pricing: response.data.pricing, followUpIds });
          // El experimento NO se ha movido todavía: sólo se llama a la API (y se avisa) cuando
          // el operador confirma los precios en el diálogo.
          return { success: false, reason: 'dialog' };
        }
      }
      let result;
      
      // Use specific functions for backward compatibility, but support all statuses
      switch (action) {
        case 'accept':
          result = await acceptExperiment(experimentId, currentUser.customer_id, pricingOverride);
          break;
        case 'begin':
        case 'running':
          result = await beginExperiment(experimentId, currentUser.customer_id);
          break;
        case 'reject':
        case 'rejected':
          result = await rejectExperiment(experimentId, reason, currentUser.customer_id);
          break;
        case 'pending':
        case 'accepted':
        case 'finished':
          // Use generic update for other status changes
          result = await updateExperimentStatus(experimentId, action, currentUser.customer_id, reason, pricingOverride);
          break;
        default:
          return;
      }

      if (result.success) {
        // Sin recap de sólo lectura: los precios ya se revisaron (y se pudieron corregir) en el
        // diálogo previo, repetirlos después sólo añade un clic.
        // Only show toast if not part of bulk operation
        if (!skipRefresh) {
          showToast(result.message, 'success');
          // Trigger data refresh after successful mutation
          await refreshDataAfterMutation();
        }
      } else {
        showToast(result.message || t('actionFailed'), 'error');
      }
      
      return result;
    } catch (error) {
      // El backend manda el motivo real (p. ej. `experimentStartsTooSoon`); enseñarlo en vez
      // de un "acción fallida" que no dice al operador qué corregir.
      showToast(getApiErrorMessage(error, t('actionFailed')), 'error');
      throw error;
    }
  };

  const handleRejectConfirm = async (reason) => {
    if (!currentExperimentId) return;
    
    try {
      const result = await rejectExperiment(currentExperimentId, reason, currentUser.customer_id);
      
      if (result.success) {
        showToast(result.message, 'success');
        
        // Trigger data refresh after successful mutation
        await refreshDataAfterMutation();
      } else {
        showToast(result.message || t('actionFailed'), 'error');
      }
    } catch (error) {
      showToast(t('actionFailed'), 'error');
    } finally {
      setShowRejectDialog(false);
      setCurrentExperimentId(null);
    }
  };

  // Alert action handlers - supports both single and bulk operations  
  const handleAlertAction = async (alertIds, action, metadata = {}) => {
    try {
      // Handle both old single alert format (action, alertId) and new bulk format (alertIds, action)
      let actualAlertIds, actualAction, actualMetadata;
      
      if (typeof alertIds === 'string' && typeof action === 'string') {
        // Old format: (action, alertId, metadata) - for backwards compatibility
        actualAction = alertIds;
        actualAlertIds = action;
        actualMetadata = metadata;
      } else {
        // New format: (alertIds, action, metadata) - matches experiment pattern
        actualAlertIds = alertIds;
        actualAction = action;  
        actualMetadata = metadata;
      }
      
      let result;
      
      switch (actualAction) {
        case 'not_broken':
          // AlertActions sends 'not_broken' when user clicks "Mark as working"
          result = await markAlertAsNotBroken(actualAlertIds, currentUser.customer_id);
          break;
          
        case 'mark_as_broken':
          // AlertActions sends 'mark_as_broken' when user submits planned date
          result = await markAlertAsBroken(actualAlertIds, actualMetadata.planned_resolution_date, currentUser.customer_id);
          break;
          
        case 'resolved':
          // AlertActions sends 'resolved' when user clicks "Mark as Repaired"
          result = await markAlertAsResolved(actualAlertIds, actualMetadata.resolution_date, currentUser.customer_id);
          break;
          
        default:
          return;
      }

      if (result.success) {
        showToast(result.message || `Alert ${actualAction} successful`, 'success');
        // Refresh data after successful action
        await refreshDataAfterMutation();
      } else {
        showToast(result.message || `Failed to ${actualAction} alert`, 'error');
      }
      
      return result;
    } catch (error) {
      console.error('Error handling alert action:', error);
      const errorAction = typeof alertIds === 'string' ? alertIds : action || 'action';
      showToast(`Error: ${errorAction} failed`, 'error');
      throw error;
    }
  };

  return (
    <Box sx={{ 
      px: 0, // Remove horizontal padding
      py: responsiveSpacing.md, // Reduce vertical padding since no title
      width: '100%',
      maxWidth: '100vw', // Ensure we use full viewport width
      boxSizing: 'border-box'
    }}>
      <AppliedPricingDialog open={Boolean(priceReview)} pricing={priceReview?.pricing} onClose={() => setPriceReview(null)} onConfirm={async (prices) => {
        const review = priceReview;
        setPriceReview(null);
        await handleExperimentAction(review.experimentId, review.action, '', false, prices);
        // Reanuda la cola del bulk que quedó detrás del diálogo. Cada id puede abrir su propio
        // diálogo — en ese caso este bucle se corta y el siguiente confirm continúa la cadena.
        const rest = review.followUpIds || [];
        for (let i = 0; i < rest.length; i += 1) {
          const result = await handleExperimentAction(rest[i], review.action, '', false, null, rest.slice(i + 1));
          if (result?.reason === 'dialog') return;
        }
      }} />

      {/* Filters */}
      <Box ref={filtersRef} sx={{ px: responsiveSpacing.xs }}>
        <ExperimentFilters
          filters={filters}
          filterOptions={filterOptions}
          groupOptions={visibleGroups}
          availableLocations={availableLocations}
          availableMachines={availableMachines}
          availableRoutes={availableRoutes}
          availableAlertTypes={availableAlertTypes}
          onFilterChange={handleFilterChange}
          onDateRangeChange={handleDateRangeChange}
          loading={loadingFilters}
          filterMode={filterMode}
          onFilterModeChange={handleFilterModeChange}
          productOptions={productOptions}
          isAlertsMode={isAlertsMode}
          routesLoading={routesLoading}
        />
      </Box>

      {/* Main Content */}
      {loading ? (
        <ExperimentBoardSkeleton isAlertsMode={isAlertsMode} />
      ) : (
        <>
          {/* No Results Message */}
          {Object.values(experiments).every(arr => arr.length === 0) && filters.dateRange.start && filters.dateRange.end ? (
            <Box sx={{
              textAlign: 'center',
              py: responsiveSpacing.xxl,
              color: colors.text.secondary
            }}>
              <Typography sx={{
                ...responsiveTypography.h4,
                mb: responsiveSpacing.sm
              }}>
                {t('noExperimentsFound')}
              </Typography>
              <Typography sx={{
                ...responsiveTypography.body
              }}>
                {t('tryAdjustingFilters')}
              </Typography>
            </Box>
          ) : (
            /* Board - Unified for experiments and alerts */
            <ExperimentBoard
              experiments={experiments}
              onExperimentAction={isAlertsMode ? handleAlertAction : handleExperimentAction}
              loading={loading}
              itemsPerPage={10}
              onDataMutation={refreshDataAfterMutation}
              clientId={currentUser.customer_id}
              summarized={true}
              isAlertsMode={isAlertsMode}
              currentFilters={filters}
              filterOptions={filterOptions}
              onCreateSuccess={showToast}
              onCreateError={(message) => showToast(message, 'error')}
              filtersRef={filtersRef}
            />
          )}
        </>
      )}

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={hideToast} 
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Reject Reason Dialog */}
      <RejectReasonDialog
        open={showRejectDialog}
        onClose={() => {
          setShowRejectDialog(false);
          setCurrentExperimentId(null);
        }}
        onConfirm={handleRejectConfirm}
        loading={false}
      />
    </Box>
  );
};

// Tab wrapper components with debug logs
export const ExperimentsTab = () => {
  return <OverviewTab isAlertsMode={false} />;
};

export const AlertsTab = () => {
  return <OverviewTab isAlertsMode={true} />;
};

// Main ExperimentManagement component - defaults to experiments
const ExperimentManagement = () => {
  return <ExperimentsTab />;
};

export default ExperimentManagement;
